import { KeywordAutoSuggest } from '@seek/discover-ui';
import { useHubble } from '@seek/hubble';
import { useTranslations } from '@vocab/react';
import { Box, IconSearch } from 'braid-design-system';
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ComponentProps,
} from 'react';

import { SavedRecentSearches } from 'src/components/Search/SearchBar/SavedRecentSearches/SavedRecentSearches';
import { getRefineSearchQuery } from 'src/components/Search/utils/utils';
import { useAnalyticsFacade } from 'src/modules/AnalyticsFacade';
import { useSavedRecentSearchesFeatureFlag } from 'src/store/featureFlags/hooks';
import { useDispatch, useSelector } from 'src/store/react';
import { clearResults } from 'src/store/results';
import {
  updateBaseKeywordParam,
  updateKeywordsField as _updateKeywordsField,
} from 'src/store/search';
import {
  selectBaseKeywords,
  selectSearchQuery,
} from 'src/store/search/selectors';
import {
  selectFeatureFlag,
  selectIsSrp,
  selectKeywordField,
} from 'src/store/selectors';
import { useMelwaysCountry } from 'src/utils/melwaysHelper';

interface KeywordFieldProps {
  onSuggestionSelected: () => void;
  onInputBlur: () => void;
  onClear: () => void;
}

export const KEYWORD_INPUT_FIELD_ID = 'keywords-input';

import translations from './.vocab';

export const MobileKeywordField = ({
  onSuggestionSelected,
  onInputBlur,
  onClear,
}: KeywordFieldProps) => {
  const { t } = useTranslations(translations);
  const analyticsFacade = useAnalyticsFacade();
  const dispatch = useDispatch();
  const keywordsField = useSelector(selectKeywordField);
  const country = useMelwaysCountry();

  const [keywordsFieldLocalState, setKeywordFieldLocalState] =
    useState(keywordsField);
  const baseKeywords = useSelector(selectBaseKeywords);
  const isSRP = useSelector(selectIsSrp);
  const query = getRefineSearchQuery(useSelector(selectSearchQuery));
  const [displaySavedRecentSearches, setDisplaySavedRecentSearches] =
    useState(false);
  const showSavedRecentInSearch = useSavedRecentSearchesFeatureFlag();

  const hubble = useHubble();

  const visitorId = hubble.visitorId();
  const isKeywordAutosuggestV2Enabled = useSelector(
    selectFeatureFlag('keywordAutosuggestV2'),
  );

  useEffect(() => {
    if (!isSRP) {
      dispatch(updateBaseKeywordParam(''));
    } else if (keywordsField && !baseKeywords) {
      dispatch(updateBaseKeywordParam(keywordsField));
    }
  }, [baseKeywords, dispatch, isSRP, keywordsField]);

  useEffect(() => {
    if (keywordsField !== keywordsFieldLocalState) {
      setKeywordFieldLocalState(keywordsField);
    }
  }, [keywordsField, keywordsFieldLocalState]);

  const updateKeywordsField = useCallback(
    (text: string) => {
      setKeywordFieldLocalState(text);
      dispatch(_updateKeywordsField(text));

      dispatch(updateBaseKeywordParam(text));

      if (showSavedRecentInSearch) {
        setDisplaySavedRecentSearches(!text);
      }
    },
    [dispatch, showSavedRecentInSearch],
  );

  const onKeywordsChange = useCallback(
    (
      ...args: Parameters<ComponentProps<typeof KeywordAutoSuggest>['onChange']>
    ) => {
      const [suggestion] = args;
      analyticsFacade.keywordChanged(
        suggestion.text,
        suggestion.isSuggestion,
        suggestion.previousText ?? '',
        suggestion.keywordListCount,
        suggestion.keywordRank,
        suggestion.suggestionMetadata,
      );
      updateKeywordsField(suggestion.text);

      if (suggestion.isSuggestion) {
        onSuggestionSelected();
      }
    },
    [analyticsFacade, onSuggestionSelected, updateKeywordsField],
  );

  const savedRecentSearchesClicked = useRef(false);
  const savedRecentSearchesRef = useRef<HTMLDivElement>(null);
  const onKeywordsBlur = () => {
    if (showSavedRecentInSearch) {
      // We're adding a small timeout so we can check if the user clicked into the saved/recent menu so we don't close it automatically
      setTimeout(() => {
        if (savedRecentSearchesClicked.current) {
          return;
        }

        const { keywords = '' } = query;

        if (keywordsField !== keywords) {
          updateKeywordsField(keywordsField);
          onInputBlur();
        }

        setDisplaySavedRecentSearches(false);
      }, 150);
    } else {
      const { keywords = '' } = query;

      if (keywordsField !== keywords) {
        updateKeywordsField(keywordsField);
        onInputBlur();
      }
    }
  };

  const onKeywordsClear = () => {
    dispatch(clearResults());
    updateKeywordsField('');
    onClear();
  };

  const onKeywordsFocus = () => {
    if (showSavedRecentInSearch) {
      setDisplaySavedRecentSearches(!keywordsFieldLocalState);
    }
  };

  return (
    <Box position="relative">
      <KeywordAutoSuggest
        keywordAutosuggestV2={isKeywordAutosuggestV2Enabled}
        visitorId={visitorId}
        keyword={{ text: keywordsFieldLocalState }}
        id={KEYWORD_INPUT_FIELD_ID}
        onChange={onKeywordsChange}
        onBlur={onKeywordsBlur}
        onClear={onKeywordsClear}
        onFocus={onKeywordsFocus}
        country={country}
        label=""
        reserveMessageSpace={false}
        showMobileBackdrop={true}
        name="keywords"
        icon={<IconSearch />}
        scrollToTopOnMobile={false}
        placeholder={t('Enter Keywords')}
      />

      {showSavedRecentInSearch && displaySavedRecentSearches ? (
        <Box
          ref={savedRecentSearchesRef}
          onBlur={() => {
            savedRecentSearchesClicked.current = false;
          }}
          onClick={() => {
            savedRecentSearchesClicked.current = true;
          }}
        >
          <SavedRecentSearches />
        </Box>
      ) : null}
    </Box>
  );
};
